'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const PrivacyPolicyInteractive = () => {
  const sections = [
    {
      id: 'introduccion',
      title: '1. Introducción',
      content: `LynMet es una plataforma de coaching empresarial impulsada por inteligencia artificial, diseñada para apoyar a emprendedores latinoamericanos en la toma de decisiones estratégicas. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos y protegemos tu información personal cuando utilizas nuestra aplicación.

Al usar LynMet, aceptas las prácticas descritas en esta política. Si no estás de acuerdo con algún aspecto, te recomendamos no utilizar nuestros servicios.`
    },
    {
      id: 'informacion-recopilada',
      title: '2. Información que Recopilamos',
      content: `**Información de cuenta:**
• Nombre completo
• Correo electrónico
• Contraseña (encriptada)
• Foto de perfil (opcional)

**Información de sesiones de coaching:**
• Respuestas a cuestionarios de diagnóstico empresarial
• Datos del negocio compartidos durante el coaching
• Historial de conversaciones con el coach IA
• Análisis de competencia y estrategias generadas
• Planes de mejora y métricas de seguimiento

**Información de uso:**
• Tokens utilizados y recargados
• Fechas y horarios de sesiones
• Funcionalidades utilizadas (scoring, análisis comercial, estratégico, etc.)

**Información de pago:**
• Procesada de forma segura por Mercado Pago
• No almacenamos números de tarjetas completos
• Solo guardamos referencias de transacciones para historial de compras`
    },
    {
      id: 'uso-informacion',
      title: '3. Cómo Usamos tu Información',
      content: `Utilizamos tus datos para:

**Prestación del servicio:**
• Generar diagnósticos empresariales personalizados
• Proporcionar recomendaciones estratégicas mediante IA
• Analizar competencia y oportunidades de mercado
• Crear planes de mejora adaptados a tu negocio

**Mejora continua:**
• Entrenar y optimizar nuestros modelos de IA
• Analizar patrones para mejorar la calidad de las recomendaciones
• Identificar áreas de mejora en la plataforma

**Comunicación:**
• Enviar notificaciones sobre recarga de tokens
• Informar sobre nuevas funcionalidades
• Responder a consultas de soporte

**Importante:** LynMet utiliza IA como herramienta de apoyo. Las recomendaciones generadas NO constituyen asesoría legal, financiera ni de inversión profesional. El usuario es el único responsable de las decisiones tomadas basándose en la información proporcionada por la plataforma.`
    },
    {
      id: 'almacenamiento',
      title: '4. Almacenamiento y Seguridad',
      content: `**Infraestructura:**
• Utilizamos Supabase (PostgreSQL) para almacenamiento seguro
• Servidores ubicados en infraestructura de AWS
• Encriptación de datos en tránsito (HTTPS/TLS)
• Encriptación de contraseñas con bcrypt

**Retención de datos:**
• Información de cuenta: mientras mantengas tu cuenta activa
• Sesiones de coaching: almacenadas de forma indefinida para tu consulta
• Datos de transacciones: retenidos por 7 años para cumplimiento fiscal
• Tokens de autenticación: expiración automática según configuración de sesión

**Respaldos:**
• Realizamos copias de seguridad periódicas
• Los respaldos se mantienen encriptados
• Recuperación de datos disponible en caso de incidentes`
    },
    {
      id: 'compartir-datos',
      title: '5. Compartir Información con Terceros',
      content: `**NO vendemos ni compartimos tus datos personales con terceros con fines comerciales.**

Únicamente compartimos información con:

**Proveedores de servicio esenciales:**
• **OpenAI**: Procesamiento de consultas de coaching mediante GPT (datos anonimizados)
• **Mercado Pago**: Procesamiento seguro de pagos
• **Supabase**: Almacenamiento y gestión de base de datos

**Todos nuestros proveedores:**
• Están obligados contractualmente a proteger tus datos
• Solo procesan información necesaria para el servicio
• Cumplen con estándares internacionales de seguridad (SOC 2, ISO 27001)
• No utilizan tus datos para entrenar modelos propios sin anonimización

**Divulgación legal:**
Solo compartiremos tus datos si es requerido por ley, orden judicial o autoridad competente.`
    },
    {
      id: 'derechos-usuario',
      title: '6. Tus Derechos',
      content: `Como usuario de LynMet, tienes derecho a:

**Acceso:**
• Solicitar una copia de toda la información que tenemos sobre ti
• Descargar tu historial de sesiones y análisis

**Rectificación:**
• Actualizar tu información de perfil en cualquier momento
• Corregir datos incorrectos

**Eliminación:**
• Solicitar la eliminación completa de tu cuenta y datos asociados
• Ten en cuenta que algunos datos pueden retenerse por obligaciones legales

**Portabilidad:**
• Exportar tus datos en formato estructurado (JSON/CSV)
• Transferir información a otra plataforma

**Oposición:**
• Rechazar el uso de tus datos para mejora de modelos de IA
• Limitar el procesamiento de información personal

Para ejercer cualquiera de estos derechos, contáctanos en: **privacidad@lynmet.com**
Responderemos a tu solicitud en un plazo máximo de 30 días.`
    },
    {
      id: 'cookies',
      title: '7. Cookies y Tecnologías Similares',
      content: `LynMet utiliza cookies y tecnologías similares para:

**Cookies esenciales:**
• Mantener tu sesión activa
• Recordar preferencias de idioma y configuración
• Autenticación segura

**Cookies de rendimiento:**
• Analizar uso de la plataforma
• Identificar errores técnicos
• Mejorar experiencia de usuario

**Gestión de cookies:**
Puedes configurar tu navegador para rechazar cookies, pero esto puede afectar la funcionalidad de la plataforma. No utilizamos cookies de terceros para publicidad.`
    },
    {
      id: 'menores',
      title: '8. Menores de Edad',
      content: `LynMet está dirigido a personas mayores de 18 años. No recopilamos intencionalmente información de menores de edad. Si descubrimos que un menor ha proporcionado datos personales, eliminaremos inmediatamente esa información de nuestros sistemas.

Si eres padre o tutor y crees que tu hijo menor de 18 años ha compartido información con nosotros, contáctanos de inmediato en: **soporte@lynmet.com**`
    },
    {
      id: 'cambios',
      title: '9. Cambios a esta Política',
      content: `Nos reservamos el derecho de actualizar esta Política de Privacidad en cualquier momento. Los cambios importantes serán notificados mediante:

• Correo electrónico a tu cuenta registrada
• Aviso destacado en la plataforma
• Actualización de la fecha "Última actualización" al inicio de esta política

Te recomendamos revisar periódicamente esta página para estar al tanto de cualquier cambio.

**Última actualización:** Enero 2026`
    },
    {
      id: 'contacto',
      title: '10. Contacto',
      content: `Si tienes preguntas, inquietudes o solicitudes relacionadas con esta Política de Privacidad o el manejo de tus datos personales, contáctanos:

**Correo electrónico:** privacidad@lynmet.com
**Tiempo de respuesta:** Máximo 48 horas hábiles
**Soporte general:** soporte@lynmet.com

Estamos comprometidos con la protección de tu privacidad y trabajamos constantemente para mantener tus datos seguros.`
    }
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(44,120,251,0.08),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(62,224,190,0.12),transparent_32%),linear-gradient(180deg,#f7fbff_0%,#ffffff_48%,#eefcf8_100%)]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="flex items-center space-x-2 group">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
                <Icon name="SparklesIcon" size={24} className="text-white" />
              </div>
              <span className="text-2xl font-black bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>LynMet</span>
            </Link>
            
            <Link
              href="/"
              className="flex items-center space-x-2 text-gray-600 hover:text-[var(--primary)] transition-colors"
            >
              <Icon name="ArrowLeftIcon" size={20} />
              <span className="font-medium">Volver al Inicio</span>
            </Link>
          </div>
        </div>
      </header>
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-[#2c78fb]/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#3ee0be]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '700ms' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="section-eyebrow mb-6">
            <Icon name="ShieldCheckIcon" size={20} className="text-[var(--primary)]" />
            <span className="text-sm font-semibold">
              Última actualización: Enero 2026
            </span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            Política de Privacidad
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            En LynMet, tu privacidad es nuestra prioridad. Conoce cómo protegemos tus datos y respetamos tu información personal.
          </p>
        </div>
      </section>
      {/* Content Sections */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="app-card rounded-3xl overflow-hidden">
          <div className="p-8 lg:p-12 space-y-12">
            {sections?.map((section, index) => (
              <div key={section?.id} id={section?.id} className="scroll-mt-20">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <span className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mr-4" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
                    {index + 1}
                  </span>
                  {section?.title?.replace(/^\d+\.\s*/, '')}
                </h2>
                <div className="prose prose-lg max-w-none pl-14">
                  {section?.content?.split('\n\n')?.map((paragraph, idx) => {
                    if (paragraph?.startsWith('**') && paragraph?.endsWith('**')) {
                      return (
                        <h3 key={idx} className="text-xl font-bold text-gray-800 mt-6 mb-3">
                          {paragraph?.replace(/\*\*/g, '')}
                        </h3>
                      );
                    }
                    if (paragraph?.startsWith('•')) {
                      return (
                        <ul key={idx} className="list-none space-y-2 my-4">
                          {paragraph?.split('\n')?.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start">
                              <Icon name="CheckCircleIcon" size={20} className="text-[var(--primary)] mr-3 mt-1 flex-shrink-0" />
                              <span className="text-gray-700">{item?.replace('• ', '')}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={idx} className="text-gray-700 leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
                {index < sections?.length - 1 && (
                  <div className="mt-8 border-b border-gray-200" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-12 rounded-3xl shadow-2xl p-8 lg:p-12 text-center text-white" style={{ backgroundImage: 'linear-gradient(135deg, #06213f 0%, #0f3f87 34%, #2c78fb 68%, #3ee0be 100%)' }}>
          <div className="max-w-2xl mx-auto">
            <Icon name="RocketLaunchIcon" size={48} className="text-white mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              ¿Listo para Transformar tu Negocio?
            </h2>
            <p className="text-xl text-white/95 mb-8">
              Comienza tu análisis empresarial con IA en menos de 5 minutos. Sin compromiso, sin tarjeta de crédito.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/register"
                className="btn-primary-app group inline-flex items-center justify-center space-x-2 px-8 py-4 text-lg"
              >
                <span>Prueba LynMet Gratis</span>
                <Icon name="ArrowRightIcon" size={22} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/ai-business-coach"
                className="btn-secondary-app-dark inline-flex items-center justify-center space-x-2 px-8 py-4 text-lg"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={22} />
                <span>Ir al Coach IA</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundImage: 'var(--gradient-primary-action)' }}>
              <Icon name="SparklesIcon" size={20} className="text-white" />
            </div>
            <span className="text-xl font-black text-white">LynMet</span>
          </div>
          <p className="text-sm mb-4">
            Coach empresarial impulsado por IA para emprendedores latinoamericanos.
          </p>
          <div className="flex justify-center space-x-6 text-sm">
            <Link href="/privacy-policy" className="hover:text-white transition-colors">
              Privacidad
            </Link>
            <Link href="/terms-conditions" className="hover:text-white transition-colors">
              Términos
            </Link>
            <Link href="/" className="hover:text-white transition-colors">
              Inicio
            </Link>
          </div>
          <p className="text-xs mt-4 text-gray-500">
            © {new Date()?.getFullYear()} LynMet. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyInteractive;
